import React, { useContext, useEffect, useState } from 'react';
import { ArrowLeftOutlined, CheckCircleFilled, EyeFilled, EyeInvisibleFilled } from '@ant-design/icons';
import { Alert, Form, Input, Select } from 'antd';
import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import { useWindowSize } from 'react-use';

import OmiButton from 'components/@Omini/OmiButton';
import BranchIntro from 'components/Auth/BranchIntro';
import { TENANT_TYPE } from 'constants/Common';
import { MIN_MD } from 'constants/ThemeSetting';
import { hideMessage, registerTenant } from 'store/actions/AuthAction';
import { AuthContext } from 'store/context';

function PharmacyIcon({ color }) {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
      {/* Capsule outline */}
      <rect x="2" y="9" width="24" height="10" rx="5" stroke={color} strokeWidth="1.8" />
      {/* Left half — filled */}
      <path d="M14 9H7a5 5 0 0 0 0 10h7V9z" fill={color} fillOpacity="0.25" />
      {/* Center divider */}
      <line x1="14" y1="9" x2="14" y2="19" stroke={color} strokeWidth="1.8" />
      {/* Rx cross on left half */}
      <path d="M8.5 13.5v3M7 15h3" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ClinicIcon({ color }) {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
      {/* ECG / heartbeat line */}
      <path
        d="M2 14h5l2.5-6 5 12 2.5-6 2 3.5H26"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const TENANT_OPTIONS = [
  {
    value: TENANT_TYPE.PHARMACY,
    label: 'Nhà thuốc',
    description: 'Dành cho nhà thuốc, quầy thuốc và chuỗi dược phẩm.',
    icon: color => <PharmacyIcon color={color} />,
    accentColor: '#16a34a',
    bgColor: '#f0fdf4',
  },
  {
    value: TENANT_TYPE.CLINIC,
    label: 'Phòng khám',
    description: 'Dành cho phòng khám đa khoa, chuyên khoa và trung tâm y tế.',
    icon: color => <ClinicIcon color={color} />,
    accentColor: '#2563eb',
    bgColor: '#eff6ff',
  },
];

const BUSINESS_TYPES = [
  { label: 'Đa khoa', value: 'general' },
  { label: 'Nha khoa', value: 'dental' },
  { label: 'Chuyên khoa', value: 'specialist' },
  { label: 'Xét nghiệm', value: 'diagnostic' },
];

export default function SignUpV2({ initialTenantType = null }) {
  const { width } = useWindowSize();
  const navigate = useNavigate();
  const {
    AuthState: { loading, error },
    AuthDispatch,
  } = useContext(AuthContext);

  const [step, setStep] = useState(initialTenantType ? 1 : 0);
  const [tenantType, setTenantType] = useState(initialTenantType);
  const [registeredEmail, setRegisteredEmail] = useState('');

  useEffect(() => {
    AuthDispatch(hideMessage());
  }, [AuthDispatch]);

  useEffect(() => {
    if (initialTenantType) {
      setTenantType(initialTenantType);
      setStep(1);
    }
  }, [initialTenantType]);

  const handleSelectType = value => {
    setTenantType(value);
  };

  const handleNextStep = () => {
    if (!tenantType) return;

    navigate(`/${tenantType}/signup-v2`);
  };

  const handleSubmit = values => {
    setRegisteredEmail(values.email);

    const payload = { ...values, tenant_type: tenantType };

    // Pass a callback instead of navigate so we show the success step in-page
    registerTenant({ payload, dispatch: AuthDispatch, navigate: () => setStep(2) });
  };

  const alertClasses = classNames({
    'omi-auth-alert': true,
    'flex items-center mt-4': error,
    hidden: !error,
  });

  const isClinic = tenantType === TENANT_TYPE.CLINIC;

  return (
    <div className="flex flex-col lg:flex-row min-h-screen">
      <div className="omi-auth-layout-title">
        <BranchIntro isSignup />
      </div>

      <div className="omi-auth-layout-content">
        <div className="flex flex-col justify-center items-center min-h-screen px-6 py-12 sm:px-12">
          <div className="omi-auth-form sign-up-container">
            {/* Step 0 — choose type */}
            {step === 0 && (
              <>
                <p className="omi-auth-form-title">Bạn đang quản lý gì?</p>
                <p className="mb-10 lg:mb-8 leading-snug">
                  Chọn loại hình phù hợp để Omini thiết lập hệ thống tốt nhất cho bạn.
                </p>

                <div className="grid grid-cols-2 gap-3 mb-6 items-stretch">
                  {TENANT_OPTIONS.map(opt => {
                    const isSelected = tenantType === opt.value;

                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleSelectType(opt.value)}
                        className="text-left rounded-md border-2 p-4 transition-all duration-150 focus:outline-none flex flex-col"
                        style={{
                          borderColor: isSelected ? opt.accentColor : '#e5e7eb',
                          backgroundColor: isSelected ? opt.bgColor : '#fff',
                        }}
                      >
                        <div className="flex items-start justify-between mb-2">
                          {opt.icon(opt.accentColor)}
                          {isSelected && <CheckCircleFilled style={{ fontSize: 16, color: opt.accentColor }} />}
                        </div>
                        <p className="font-semibold text-grey-900 text-sm mb-1">{opt.label}</p>
                        <p className="text-grey-700 text-xs">{opt.description}</p>
                      </button>
                    );
                  })}
                </div>

                <OmiButton type="primary" block disabled={!tenantType} onClick={handleNextStep}>
                  Tiếp tục
                </OmiButton>

                <div className="flex justify-end mt-4">
                  <Link to="/login" className="f-semibold text-grey-700 text-xs sm:text-sm">
                    Trở về đăng nhập
                  </Link>
                </div>
              </>
            )}

            {/* Step 1 — registration form (same as SignUp.js + clinic extras) */}
            {step === 1 && (
              <>
                <button
                  type="button"
                  onClick={() => (initialTenantType ? navigate('/signup-v2') : setStep(0))}
                  className="flex items-center gap-2 text-grey-700 hover:text-grey-900 font-medium transition-colors mb-4"
                >
                  <ArrowLeftOutlined />
                  <span className="font-medium">Quay lại</span>
                </button>
                <p className="omi-auth-form-title" style={{ margin: 0 }}>
                  Đăng ký miễn phí
                </p>
                <p className="mb-6 lg:mb-8">Tạo tài khoản trong 1 phút và bán hàng miễn phí ngay!</p>

                <Form
                  name="sign-up-v2"
                  size={width >= MIN_MD ? 'large' : 'middle'}
                  layout="vertical"
                  onFinish={handleSubmit}
                >
                  <Form.Item
                    name="name"
                    label={isClinic ? 'Tên phòng khám' : 'Tên cửa hàng'}
                    rules={[
                      { required: true, message: `Yêu cầu nhập ${isClinic ? 'tên phòng khám' : 'tên cửa hàng'}.` },
                    ]}
                  >
                    <Input placeholder={isClinic ? 'Vd: Phòng khám Đa khoa Omini' : 'Vd: Nhà thuốc Omini'} />
                  </Form.Item>

                  {isClinic && (
                    <Form.Item
                      name="business_type"
                      label="Loại phòng khám"
                      rules={[{ required: true, message: 'Vui lòng chọn loại phòng khám.' }]}
                    >
                      <Select placeholder="Chọn loại phòng khám">
                        {BUSINESS_TYPES.map(t => (
                          <Select.Option key={t.value} value={t.value}>
                            {t.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}

                  <div className="inline-block w-1/2 pr-2">
                    <Form.Item label="Họ" name="last_name" rules={[{ required: true, message: 'Yêu cầu nhập họ.' }]}>
                      <Input placeholder="Vd: Nguyễn Văn" />
                    </Form.Item>
                  </div>
                  <div className="inline-block w-1/2 pl-2">
                    <Form.Item label="Tên" name="first_name" rules={[{ required: true, message: 'Yêu cầu nhập tên.' }]}>
                      <Input placeholder="Vd: A" />
                    </Form.Item>
                  </div>

                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { type: 'email', message: 'Định dạng email không đúng. Vui lòng kiểm tra lại.' },
                      { required: true, message: 'Yêu cầu nhập email.' },
                    ]}
                  >
                    <Input placeholder="hello@omini.vn" />
                  </Form.Item>

                  <Form.Item
                    name="tel"
                    label="Số điện thoại"
                    rules={[{ required: true, message: 'Yêu cầu nhập số điện thoại.' }]}
                  >
                    <Input placeholder="Số điện thoại" />
                  </Form.Item>

                  <Form.Item
                    label="Mật khẩu"
                    name="password"
                    rules={[{ required: true, message: 'Yêu cầu nhập mật khẩu.' }]}
                  >
                    <Input.Password
                      iconRender={visible =>
                        visible ? <EyeFilled width={18} height={18} /> : <EyeInvisibleFilled width={18} height={18} />
                      }
                      placeholder="Mật khẩu"
                    />
                  </Form.Item>

                  <Form.Item className="mt-10 md:mt-6">
                    <Alert className={alertClasses} message={error} type="error" showIcon />
                    <OmiButton type="primary" className="omi-auth-button" htmlType="submit" block loading={loading}>
                      Tạo tài khoản miễn phí
                    </OmiButton>
                  </Form.Item>

                  <p className="hidden sm:block text-center mb-12">
                    Khi nhấn vào nút &quot;Tạo tài khoản miễn phí&quot; đồng nghĩa với việc bạn đã đồng ý các{' '}
                    <span className="text-blue-600 cursor-pointer">điều khoản</span> và{' '}
                    <span className="text-blue-600 cursor-pointer">chính sách</span> của Omini
                  </p>
                </Form>
              </>
            )}

            {/* Step 2 — success */}
            {step === 2 && (
              <div className="flex flex-col items-center py-8 text-center">
                <CheckCircleFilled style={{ fontSize: 56, color: '#16a34a' }} className="mb-4" />
                <p className="omi-auth-form-title">Đăng ký thành công!</p>
                <p className="text-grey-500 mb-1">
                  Chúng tôi đã gửi email xác nhận đến <strong>{registeredEmail}</strong>
                </p>
                <p className="text-grey-400 text-sm mb-8">
                  Vui lòng kiểm tra hộp thư và nhấn vào đường dẫn để kích hoạt tài khoản.
                </p>
                <OmiButton type="primary" onClick={() => navigate('/login')}>
                  Về trang đăng nhập
                </OmiButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
